import { DownOutlined, UpOutlined } from '@ant-design/icons';
import { type TabsProps, Tabs } from 'antd';
import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Tooltip } from 'components/ui';

const PageHeaderWrapper = styled.div`
  background: ${({ theme }) => theme.secondaryBackgroundColor};
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
`;

const Detail = styled.div<{ $paddingBottom?: boolean }>`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-content: center;
  padding: 1rem 2em 0 2rem;
  padding-bottom: ${({ $paddingBottom }) => ($paddingBottom ? '1rem' : '0')};
`;

const MainContent = styled.div`
  flex: 1;
`;

export const PageHeaderActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CollapseToggleDivider = styled.div`
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  height: 0;
  position: relative;
`;

const CollapseToggle = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.secondaryBackgroundColor};
  border: 3px solid ${({ theme }) => theme.borderColor};
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  color: ${({ theme }) => theme.headerColor};
  cursor: pointer;
  display: flex;
  font-size: 0.7rem;
  height: 1.75rem;
  justify-content: center;
  line-height: 0;
  padding: 0;
  pointer-events: auto;
  position: absolute;
  left: 50%;
  top: 0;
  transform: translate(-50%, -50%);
  width: 1.75rem;
  z-index: 1;
  transition:
    background-color 120ms ease,
    border-color 120ms ease,
    color 120ms ease;

  &:hover {
    background: ${({ theme }) => theme.tertiaryBackgroundColor};
    border-color: ${({ theme }) => theme.headerColor};
    color: ${({ theme }) => theme.headerColor};
  }
`;

const StyledTabs = styled(Tabs)<{ $withTopBorder: boolean }>`
  border-top: ${({ $withTopBorder, theme }) =>
    $withTopBorder ? `1px solid ${theme.borderColor}` : 'none'};
  color: ${({ theme }) => theme.headerColor};
  &.ant-tabs {
    padding-left: 1rem;
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    text-shadow: none;
  }

  .ant-tabs-nav {
    margin-bottom: 1px;

    &::before {
      border-color: ${({ theme }) => theme.borderColor};
    }
  }
`;

export interface PageHeaderProps {
  content?: React.ReactNode;
  extraContent?: React.ReactNode;
  onTabChange?: (tabKey: string) => void;
  tabActiveKey?: string;
  tabBarExtraContent?: TabsProps['tabBarExtraContent'];
  tabList?: TabsProps['items'];
  onExpandedChange?: (isExpanded: boolean) => void;
}

const PageHeader = memo(
  ({
    content,
    extraContent,
    onTabChange,
    tabActiveKey,
    tabBarExtraContent,
    tabList,
    onExpandedChange,
  }: PageHeaderProps) => {
    const { t } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(true);
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);
    const hasDetails = Boolean(content || extraContent);

    const onChange = (key: string) => {
      if (onTabChange) {
        onTabChange(key);
      }
    };

    const details = (
      <Detail $paddingBottom={!tabList} data-testid="page-header-detail">
        <MainContent data-testid="page-header-main-content">{content}</MainContent>
        {extraContent}
      </Detail>
    );

    return (
      <PageHeaderWrapper id="page-header-component" data-testid="page-header">
        {hasDetails && (
          <>
            {isExpanded && details}
            <CollapseToggleDivider>
              <Tooltip
                title={
                  isExpanded
                    ? t('pageHeader.collapseHeader', 'Collapse header')
                    : t('pageHeader.expandHeader', 'Expand header')
                }
                open={isTooltipOpen}
                onOpenChange={setIsTooltipOpen}
              >
                <CollapseToggle
                  type="button"
                  aria-label={
                    isExpanded
                      ? t('pageHeader.collapseContent', 'Collapse header content')
                      : t('pageHeader.expandContent', 'Expand header content')
                  }
                  onClick={() => {
                    setIsTooltipOpen(false);
                    setIsExpanded(!isExpanded);
                    onExpandedChange?.(!isExpanded);
                  }}
                  data-testid="page-header-collapse-toggle"
                >
                  {isExpanded ? <UpOutlined /> : <DownOutlined />}
                </CollapseToggle>
              </Tooltip>
            </CollapseToggleDivider>
          </>
        )}
        {tabList && !!tabList.length && (
          <StyledTabs
            $withTopBorder={!hasDetails}
            activeKey={tabActiveKey}
            tabBarStyle={{ marginBottom: '0' }}
            onChange={onChange}
            tabBarExtraContent={tabBarExtraContent}
            items={tabList.map(item => ({ className: `testid-tab-pane-${item.key}`, ...item }))}
          />
        )}
      </PageHeaderWrapper>
    );
  }
);

export default PageHeader;
